
import { useState, useMemo } from "react";
import { getStudentsWithLateAlerts } from "@/utils/reportFilters";
import { LateAttendanceTable } from "../tables/LateAttendanceTable";
import { LateAttendanceTopStudents } from "../sections/LateAttendanceTopStudents";
import { AttendanceReportFilters } from "../filters/AttendanceReportFilters";
import { LatenessOverviewCard } from "../stats/LatenessOverviewCard";
import { LateAttendanceStatsCard } from "../stats/LateAttendanceStatsCard";
import { LateColorLegend } from "../legends/LateColorLegend";
import { useLateAttendanceStats } from "@/hooks/useLateAttendanceStats";
import { useRealSchoolSettings } from "@/hooks/useRealSchoolSettings";
import { Button } from "@/components/ui/button";
import { FileDown, Loader2 } from "lucide-react";
import { generateLateAttendancePDF } from "@/utils/lateAttendancePdfGenerator";
import { toast } from "@/hooks/use-toast";

interface LateAttendanceReportTabProps {
  filteredStudents: any[];
  mostLateStudents: any[];
  filteredLateRecords: any[];
  startDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (date: Date | undefined) => void;
  selectedStage: string;
  handleStageChange: (value: string) => void;
  selectedGrade: string;
  handleGradeChange: (value: string) => void;
  selectedSection: string;
  setSelectedSection: (value: string) => void;
  hasMultipleStages: boolean;
  validEnabledStages: any[];
  safeGetStageDisplayName: (stage: any) => string;
  filteredAvailableGrades: string[];
  availableSectionsForGrade: string[];
  periodDateRange?: { startDate: Date; endDate: Date };
}

export const LateAttendanceReportTab = ({
  filteredStudents,
  mostLateStudents,
  filteredLateRecords,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  selectedStage,
  handleStageChange,
  selectedGrade,
  handleGradeChange,
  selectedSection,
  setSelectedSection,
  hasMultipleStages,
  validEnabledStages,
  safeGetStageDisplayName,
  filteredAvailableGrades,
  availableSectionsForGrade,
  periodDateRange
}: LateAttendanceReportTabProps) => {
  const { settings } = useRealSchoolSettings();
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Calculate statistics using the custom hook
  const lateAttendanceStats = useLateAttendanceStats(filteredStudents, filteredLateRecords);

  // Calculate best attendance students (least late students)
  const bestAttendanceStudents = useMemo(() => {
    return [...filteredStudents]
      .sort((a, b) => (a.lateDays || 0) - (b.lateDays || 0) || (b.attendancePercentage || 0) - (a.attendancePercentage || 0))
      .slice(0, 5);
  }, [filteredStudents]);

  // Students with late alerts based on settings
  const studentsWithLateAlerts = useMemo(() => 
    getStudentsWithLateAlerts(filteredStudents, settings?.alert_rules),
    [filteredStudents, settings?.alert_rules]
  );

  // Get period display text
  const getPeriodDisplayText = () => {
    if (startDate && endDate) {
      return `من ${startDate.toLocaleDateString('ar-SA')} إلى ${endDate.toLocaleDateString('ar-SA')}`;
    } else if (startDate) {
      return `من ${startDate.toLocaleDateString('ar-SA')}`;
    }
    return "جميع الفترات";
  };

  // Handle PDF export for late attendance
  const handleDownloadLatePDF = async () => {
    // التحقق من وجود تاريخ البداية الإجباري
    if (!startDate) {
      toast({
        title: "تاريخ مطلوب",
        description: "يجب تحديد تاريخ البداية لإنشاء التقرير",
        variant: "destructive",
      });
      return;
    }

    if (filteredStudents.length === 0) {
      toast({
        title: "لا توجد بيانات",
        description: "لا توجد بيانات طلاب للتصدير",
        variant: "destructive",
      });
      return;
    }

    setIsDownloading(true);
    
    try {
      
      toast({
        title: "بدء التحميل",
        description: "جاري إنشاء تقرير التأخر PDF...",
      });

      const lateStudentsData = filteredStudents.map(student => ({
        student: student,
        lateRecords: filteredLateRecords.filter(record => record.studentId === student.id),
        totalLateDays: student.lateDays || 0,
        consecutiveLateDays: student.consecutiveLateDays || 0
      }));

      const filters = {
        selectedPeriod: getPeriodDisplayText(),
        selectedStage: selectedStage || "جميع المراحل",
        selectedGrade: selectedGrade || "جميع الصفوف", 
        selectedSection: selectedSection || "جميع الشعب"
      };

      await generateLateAttendancePDF(lateStudentsData, filters);
      
      toast({
        title: "تم التنزيل بنجاح",
        description: "تم تنزيل تقرير التأخر بصيغة PDF",
      });
    } catch (error) {
      console.error('❌ خطأ في تنزيل تقرير التأخر:', error);
      toast({
        title: "خطأ في التنزيل",
        description: error instanceof Error ? error.message : "حدث خطأ أثناء تنزيل تقرير التأخر",
        variant: "destructive",
      });
    } finally {
      
      setIsDownloading(false);
    }
  };
  // console.log('lateAttendanceStats', lateAttendanceStats)
  // console.log('bestAttendanceStudents', bestAttendanceStudents)

  return (
    <div className="space-y-6" dir="rtl">
      {/* زر التنزيل مرئي دائماً لكن غير نشط حتى تحديد جميع الشروط */}
      <div className="flex justify-end mb-6">
        <Button
          onClick={handleDownloadLatePDF}
          className="bg-primary-blue hover:bg-primary-blue/90 text-white disabled:opacity-50 disabled:cursor-not-allowed"
          size="sm"
          disabled={
            filteredStudents.length === 0 || 
            isDownloading || 
            !startDate || 
            !selectedGrade || 
            !selectedSection || 
            (hasMultipleStages && !selectedStage)
          }
        >
          {isDownloading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin text-orange-600" />
              <span className="text-orange-600">جاري التحميل...</span>
            </>
          ) : (
            <>
              <FileDown className="h-4 w-4 mr-2" />
              تنزيل تقرير PDF
            </>
          )}
        </Button>
      </div>

      <AttendanceReportFilters
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedStage={selectedStage}
        handleStageChange={handleStageChange}
        selectedGrade={selectedGrade}
        handleGradeChange={handleGradeChange}
        selectedSection={selectedSection}
        setSelectedSection={setSelectedSection}
        validEnabledStages={validEnabledStages}
        safeGetStageDisplayName={safeGetStageDisplayName}
        filteredAvailableGrades={filteredAvailableGrades}
        availableSectionsForGrade={availableSectionsForGrade}
        isLateReport={true}
        filterTitle="فلتر تقرير التأخر"
      />

      {/* عرض البيانات فقط عند تحديد تاريخ البداية */}
      {startDate ? (
        <>
          <LateAttendanceStatsCard 
            stats={lateAttendanceStats} 
            studentsWithLateAlerts={studentsWithLateAlerts.length}
          />

          <LateAttendanceTable
            studentsWithAttendance={filteredStudents}
            lateRecords={filteredLateRecords}
            enabledStages={validEnabledStages}
          />

          <LateColorLegend alertSettings={settings?.alert_rules} />

          <LateAttendanceTopStudents
            bestAttendanceStudents={bestAttendanceStudents}
            mostLateStudents={mostLateStudents}
            studentsWithLateAlerts={studentsWithLateAlerts}
            selectedPeriod={getPeriodDisplayText()}
          />

          <LatenessOverviewCard 
            filteredStudents={filteredStudents}
            selectedPeriod={getPeriodDisplayText()}
            lateRecords={filteredLateRecords}
            periodDateRange={periodDateRange}
          />
        </>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <p className="text-gray-500 text-lg">يرجى تحديد تاريخ البداية لعرض تقرير التأخر</p>
        </div>
      )}
    </div>
  );
};
